import { useMemo } from 'react';

import {
  Modal as MantineModal,
  ModalProps as MantineModalProps,
  useMantineTheme,
  useModalsStack,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import classes from './Modal.module.css';
import { ModalAffixedFooter } from './ModalAffixedFooter';
import { ModalHiddenScrollArea } from './ModalHiddenScrollArea';
import { ModalImageHeader } from './ModalImageHeader';

type ModalStackReturnType<T extends string> = ReturnType<typeof useModalsStack<T>>;

export interface ModalProps extends MantineModalProps {
  variant?: 'right-aligned' | 'full-screen-mobile';
}

const ModalDefaults = MantineModal.extend({
  defaultProps: {
    radius: 'md',
    padding: 'lg',
    size: 'md',

    closeButtonProps: {
      size: 'md',
    },
    overlayProps: {
      backgroundOpacity: 0.5,
    },
  },
  classNames: {
    root: classes.root,
    content: classes.content,
    inner: classes.inner,
    header: classes.header,
    title: classes.title,
    body: classes.body,
    close: classes.close,
    overlay: classes.overlay,
  },
});

const Modal_ = (props: ModalProps) => {
  const theme = useMantineTheme();
  const isMobileScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const fullScreenMobileProps: Pick<MantineModalProps, 'transitionProps' | 'fullScreen'> =
    useMemo(() => {
      const mobileProps = {
        transitionProps: { transition: 'slide-up' },
        fullScreen: true,
      };
      const defaultProps = theme.components.Modal.defaultProps as ModalProps;
      if (!isMobileScreen) {
        return {};
      }
      if (props.variant === 'full-screen-mobile') {
        return mobileProps;
      } else if (!props.variant && defaultProps.variant === 'full-screen-mobile') {
        return mobileProps;
      }
      return props;
    }, [isMobileScreen, props, theme.components.Modal.defaultProps]);
  return <MantineModal {...fullScreenMobileProps} {...props} />;
};

Modal_.ImageHeader = ModalImageHeader;
Modal_.AffixedFooter = ModalAffixedFooter;
Modal_.HiddenScrollArea = ModalHiddenScrollArea;

const Modal = Object.assign(Modal_, MantineModal) as typeof MantineModal & typeof Modal_;

export { Modal, ModalDefaults };
export type { ModalStackReturnType };
